import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Paper,
    Typography,
    Container,
    Box,
    Collapse,
    Checkbox,
    FormControlLabel,
    SelectChangeEvent
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../themes/ThemeContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Helper functions
const validatePassword = (password: string) =>
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(password);

const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Signup: React.FC = () => {
    const navigate = useNavigate();
    const { mode } = useAppTheme();

    const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
    const [states, setStates] = useState<{ id: number; name: string }[]>([]);
    const [timezones, setTimezones] = useState<{ id: number; timezone: string }[]>([]);

    const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
    const [agree, setAgree] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    const [formData, setFormData] = useState({
        fullname: '',
        company: '',
        email: '',
        password: '',
        confirmPassword: '',
        country: '',
        state: '',
        timezone: '',
        referralCode: '',
        usageNotes: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        axios.get('/lookup/countries').then(res => setCountries(res.data));
        axios.get('/lookup/timezones').then(res => setTimezones(res.data));
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            axios.get(`/lookup/states/${selectedCountry}`).then(res => setStates(res.data));
        } else {
            setStates([]);
        }
    }, [selectedCountry]);

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectChange = (event: SelectChangeEvent) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name as string]: value }));
        setErrors(prev => ({ ...prev, [name as string]: '' }));

        if (name === 'country') {
            setSelectedCountry(Number(value));
        }
    };


    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.fullname) newErrors.fullname = 'Full name is required';
        if (!formData.email || !validateEmail(formData.email)) newErrors.email = 'Invalid email';
        if (!formData.password || !validatePassword(formData.password)) newErrors.password = 'Password must be 8+ chars with uppercase, lowercase, number & special char';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        if (!formData.company) newErrors.company = 'Company name required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.timezone) newErrors.timezone = 'Timezone required';
        if (!agree) newErrors.agree = 'You must agree to terms';

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) return;

        try {
            await axios.post('/auth/signup', formData);
            alert("Signup successful! Redirecting to login.");
            navigate('/login');
        } catch (err) {
            setErrors({ email: 'Signup failed. Try again later.' });
        }
    };

    return (
        <>
            <Header />
            <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" gutterBottom align="center">
                        Get Started Today!
                    </Typography>
                    <Typography variant="body1" gutterBottom align="center">
                        Signup today to start your own trial of Next Era Office.
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Box mt={4} display="flex" flexDirection="column" gap={3}>
                            {/* Section 1 */}
                            <Box>
                                <Typography variant="h6">Tell us about you</Typography>
                                <TextField
                                    label="Full Name" name="fullname" fullWidth required
                                    value={formData.fullname} onChange={handleChange}
                                    error={!!errors.fullname} helperText={errors.fullname}
                                    sx={{ mt: 2 }}
                                />
                                <TextField
                                    label="Email" name="email" fullWidth required
                                    value={formData.email} onChange={handleChange}
                                    error={!!errors.email} helperText={errors.email}
                                    sx={{ mt: 2 }}
                                />
                                <TextField
                                    label="Password" type="password" name="password" fullWidth required
                                    value={formData.password} onChange={handleChange}
                                    error={!!errors.password} helperText={errors.password}
                                    sx={{ mt: 2 }}
                                />
                                <TextField
                                    label="Confirm Password" type="password" name="confirmPassword" fullWidth required
                                    value={formData.confirmPassword} onChange={handleChange}
                                    error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                                    sx={{ mt: 2 }}
                                />
                            </Box>

                            {/* Section 2 */}
                            <Box>
                                <Typography variant="h6">Tell us about how you will use Next Era Office</Typography>
                                <TextField
                                    label="Company Name" name="company" fullWidth required
                                    value={formData.company} onChange={handleChange}
                                    error={!!errors.company} helperText={errors.company}
                                    sx={{ mt: 2 }}
                                />

                                <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.state}>
                                    <InputLabel>State</InputLabel>
                                    <Select name="state" value={formData.state} onChange={handleSelectChange}>
                                        {states.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.timezone}>
                                    <InputLabel>Time Zone</InputLabel>
                                    <Select name="timezone" value={formData.timezone} onChange={handleSelectChange}>
                                        {timezones.map(tz => <MenuItem key={tz.id} value={tz.id}>{tz.timezone}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="What are you hoping to use NEO for?"
                                    name="usageNotes"
                                    multiline rows={4}
                                    placeholder="** This is completely optional, but we do appreciate it."
                                    value={formData.usageNotes}
                                    onChange={handleChange}
                                    sx={{ mt: 2 }}
                                />
                            </Box>

                            {/* Section 3 */}
                            <Box>
                                <Typography variant="h6">Final details</Typography>

                                <TextField
                                    label="Referral Code / Who referred you?"
                                    name="referralCode"
                                    fullWidth
                                    value={formData.referralCode}
                                    onChange={handleChange}
                                    sx={{ mt: 2 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={agree}
                                            onChange={() => setAgree(!agree)}
                                            name="agree"
                                        />
                                    }
                                    label={
                                        <>
                                            I agree to the&nbsp;
                                            <span
                                                onClick={() => setShowTerms(!showTerms)}
                                                style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                terms of service
                                            </span>
                                        </>
                                    }
                                    sx={{ mt: 2 }}
                                />

                                {errors.agree && (
                                    <Typography color="error" variant="body2">
                                        {errors.agree}
                                    </Typography>
                                )}

                                <Collapse in={showTerms}>
                                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography variant="body2">
                                            By signing up, you are agreeing to allow us to collect telemetry and other data.
                                            Please see our <a href="/privacy-policy" target="_blank">privacy policy</a> and <a href="/terms-of-service" target="_blank">terms of service</a> for more details.
                                        </Typography>
                                    </Box>
                                </Collapse>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ mt: 3 }}
                                >
                                    Signup
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </Paper>
            </Container>
            <Footer />
        </>
    );
};

export default Signup;
